/**
 * Signing in against Cognito, keeping the token fresh, and changing a password.
 *
 * This talks to the Cognito IdP API directly with `fetch` rather than pulling in
 * `amazon-cognito-identity-js`. That library is around 130 kB against a 4 kB app and needs
 * Node polyfills under Vite, which is a poor trade for a handful of unsigned JSON calls.
 *
 * The cost of doing it this way is the flow: `USER_PASSWORD_AUTH` sends the password to
 * Cognito over TLS, where SRP would prove knowledge of it without transmitting it. For a
 * private household app talking straight to the identity provider that is an acceptable
 * trade, and it is a deliberate one — if it stops being acceptable, replace this module with
 * the SRP library and drop `userPassword` from the client in `infra/lib/auth.ts`.
 */

import type { Credentials, Session, Tokens } from '../api/session';

/** Refresh this long before the token actually lapses, so a slow request cannot overtake it. */
const REFRESH_MARGIN_MS = 2 * 60 * 1000;

const SERVICE = 'AWSCognitoIdentityProviderService';

export interface CognitoConfig {
  region: string;
  clientId: string;
}

/** Something Cognito refused, in terms the household can act on. */
export class CognitoError extends Error {
  constructor(
    message: string,
    /** Cognito's own name for the failure, e.g. `NotAuthorizedException`. */
    readonly kind: string | null,
  ) {
    super(message);
    this.name = 'CognitoError';
  }

  /** Whether the household simply got their details wrong, as opposed to something breaking. */
  get isRefusal(): boolean {
    return this.kind === 'NotAuthorizedException' || this.kind === 'UserNotFoundException';
  }
}

/** Who the ID token says is signed in. */
export interface Identity {
  email: string;
  householdId: string;
}

interface AuthResult {
  AuthenticationResult?: {
    IdToken: string;
    AccessToken: string;
    RefreshToken?: string;
    ExpiresIn: number;
  };
  ChallengeName?: string;
}

interface Claims {
  email?: string;
  'custom:household_id'?: string;
}

export class Cognito implements Credentials {
  constructor(
    private readonly config: CognitoConfig,
    private readonly session: Session,
  ) {}

  async signIn(email: string, password: string): Promise<void> {
    const result = await this.initiate('USER_PASSWORD_AUTH', {
      USERNAME: email,
      PASSWORD: password,
    });

    const authenticated = result.AuthenticationResult;
    if (authenticated === undefined) {
      // A new member invited by hand has to set their own password first, and Cognito says so
      // by answering with a challenge instead of tokens.
      throw new CognitoError(
        `this account needs to finish setting up (${result.ChallengeName ?? 'unknown step'})`,
        result.ChallengeName ?? null,
      );
    }

    this.session.remember(asTokens(authenticated, ''));
  }

  signOut(): void {
    this.session.forget();
  }

  get isSignedIn(): boolean {
    return this.session.tokens() !== null;
  }

  /**
   * Replaces the password on the signed-in account.
   *
   * The existing tokens keep working afterwards — Cognito does not revoke them — so there is
   * nothing to re-sign-in for, and making somebody do it after they have just proved they know
   * both passwords would be friction for its own sake.
   */
  async changePassword(current: string, replacement: string): Promise<void> {
    const token = await this.access();
    if (token === null) {
      throw new CognitoError('signed out — sign in again to change your password', null);
    }

    await this.call('ChangePassword', {
      AccessToken: token,
      PreviousPassword: current,
      ProposedPassword: replacement,
    });
  }

  /** What the ID token says about the account, or `null` when there is no usable token. */
  identity(): Identity | null {
    const held = this.session.tokens();
    if (held === null) {
      return null;
    }

    const claims = readClaims(held.idToken);
    if (claims === null) {
      return null;
    }

    return {
      email: claims.email ?? 'this account',
      householdId: claims['custom:household_id'] ?? '',
    };
  }

  /**
   * A token good for the next few minutes, refreshed if need be.
   *
   * Returns `null` rather than throwing when there is nothing to work with, because "signed
   * out" is an ordinary state for this app and not an error the caller should have to catch.
   */
  async bearer(): Promise<string | null> {
    return (await this.current())?.idToken ?? null;
  }

  private async access(): Promise<string | null> {
    return (await this.current())?.accessToken ?? null;
  }

  private async current(): Promise<Tokens | null> {
    const held = this.session.tokens();
    if (held === null) {
      return null;
    }

    if (Date.now() < held.expiresAt - REFRESH_MARGIN_MS) {
      return held;
    }

    return this.refresh(held);
  }

  private async refresh(held: Tokens): Promise<Tokens | null> {
    let result: AuthResult;
    try {
      result = await this.initiate('REFRESH_TOKEN_AUTH', { REFRESH_TOKEN: held.refreshToken });
    } catch (error) {
      // A refresh token that Cognito has rejected will not start working again, so the
      // household has to sign in. A network failure is a different matter: keep the token and
      // let the caller fail as offline, or a dropped connection would sign everyone out.
      if (error instanceof CognitoError && error.isRefusal) {
        this.session.forget();
        return null;
      }
      throw error;
    }

    const authenticated = result.AuthenticationResult;
    if (authenticated === undefined) {
      this.session.forget();
      return null;
    }

    // A refresh answer carries no new refresh token; the existing one keeps running.
    const refreshed = asTokens(authenticated, held.refreshToken);
    this.session.remember(refreshed);

    return refreshed;
  }

  private initiate(flow: string, parameters: Record<string, string>): Promise<AuthResult> {
    return this.call<AuthResult>('InitiateAuth', {
      AuthFlow: flow,
      ClientId: this.config.clientId,
      AuthParameters: parameters,
    });
  }

  private async call<T>(operation: string, body: unknown): Promise<T> {
    const response = await fetch(`https://cognito-idp.${this.config.region}.amazonaws.com/`, {
      method: 'POST',
      headers: {
        'content-type': 'application/x-amz-json-1.1',
        'x-amz-target': `${SERVICE}.${operation}`,
      },
      body: JSON.stringify(body),
    });

    const answer = (await response.json().catch(() => ({}))) as T & {
      __type?: string;
      message?: string;
    };

    if (!response.ok) {
      const kind = answer.__type?.split('#').pop() ?? null;
      throw new CognitoError(explain(kind, answer.message), kind);
    }

    return answer;
  }
}

function asTokens(
  authenticated: NonNullable<AuthResult['AuthenticationResult']>,
  fallbackRefresh: string,
): Tokens {
  return {
    idToken: authenticated.IdToken,
    accessToken: authenticated.AccessToken,
    refreshToken: authenticated.RefreshToken ?? fallbackRefresh,
    expiresAt: Date.now() + authenticated.ExpiresIn * 1000,
  };
}

/**
 * The claims inside a JWT, without verifying it.
 *
 * Reading without checking the signature is right here and only here: this is used to show the
 * household its own email address, never to decide what anyone may do. Every such decision is
 * made by API Gateway, which does verify.
 */
function readClaims(token: string): Claims | null {
  const payload = token.split('.')[1];
  if (payload === undefined) {
    return null;
  }

  try {
    const padded = payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), '=');
    return JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/'))) as Claims;
  } catch {
    return null;
  }
}

/** Cognito's wording is for developers; this is for whoever is holding the phone. */
function explain(kind: string | null, detail: string | undefined): string {
  switch (kind) {
    case 'NotAuthorizedException':
    case 'UserNotFoundException':
      return 'that email and password did not match';
    case 'PasswordResetRequiredException':
      return 'this account needs its password reset before signing in';
    case 'InvalidPasswordException':
      return detail ?? 'that password does not meet the requirements';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return 'too many attempts just now — wait a moment and try again';
    default:
      return detail ?? 'could not reach the sign-in service';
  }
}
